/**
 * Pure state helpers for the "Today's lesson" card.
 * Kept free of React so Jest can exercise the empty/generating/ready/rated
 * machine without mounting the full audio player.
 */

import type { LessonSummary, SelfRating } from "@/lib/lumi-api";

export type LessonCardPhase =
  | "loading"
  | "empty"
  | "generating"
  | "ready"
  | "failed"
  | "rating"
  | "rated";

export interface DeriveLessonCardPhaseInput {
  loading: boolean;
  /** Newest lesson for the current language, or null if none exist. */
  lesson: Pick<LessonSummary, "status" | "self_rating"> | null;
  /** User finished playback (or tapped Finish) and has not yet rated. */
  awaitingRating: boolean;
  /** Local confirmation after a successful POST result. */
  justRated: boolean;
}

/**
 * Map server lesson + local UI flags → a single display phase.
 *
 * Priority: loading → (local rated confirm) → rating prompt → server status.
 */
export function deriveLessonCardPhase(
  input: DeriveLessonCardPhaseInput,
): LessonCardPhase {
  if (input.loading) {
    return "loading";
  }

  const lesson = input.lesson;
  if (!lesson) {
    return "empty";
  }

  const status = (lesson.status ?? "").toLowerCase();

  if (status === "pending" || status === "generating") {
    return "generating";
  }

  if (status === "failed") {
    return "failed";
  }

  // Ready (or unknown-but-present): local rating UX first.
  if (input.justRated || lesson.self_rating) {
    return "rated";
  }

  if (input.awaitingRating) {
    return "rating";
  }

  if (status === "ready") {
    return "ready";
  }

  // Fallback: treat unknown status like ready when we have a row.
  return "ready";
}

export function isGeneratingStatus(status: string | null | undefined): boolean {
  const s = (status ?? "").toLowerCase();
  return s === "pending" || s === "generating";
}

export function formatLessonTitle(
  lesson: Pick<LessonSummary, "seq" | "title">,
): string {
  const title = lesson.title?.trim();
  if (title) {
    return title;
  }
  return `Lesson ${lesson.seq}`;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function formatPlaybackTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const SELF_RATING_OPTIONS: Array<{
  value: SelfRating;
  label: string;
}> = [
  { value: "most", label: "Most" },
  { value: "half", label: "Half" },
  { value: "few", label: "Few" },
];

export function selfRatingLabel(
  rating: string | null | undefined,
): string | null {
  if (!rating) return null;
  const hit = SELF_RATING_OPTIONS.find((o) => o.value === rating);
  return hit?.label ?? rating;
}

/**
 * Canonical lesson language for the API (`zh` / `ja`).
 * Prefers annotatable source languages from books, then target, else `zh`.
 */
export function resolveLessonLanguage(
  books: Array<{
    source_language?: string | null;
    target_language?: string | null;
  }>,
): string {
  const counts = new Map<string, number>();

  const bump = (raw: string | null | undefined) => {
    const canon = normalizeLessonLang(raw);
    if (!canon) return;
    counts.set(canon, (counts.get(canon) ?? 0) + 1);
  };

  for (const b of books) {
    bump(b.source_language);
  }
  // Only fall back to target if we saw no source candidates.
  if (counts.size === 0) {
    for (const b of books) {
      bump(b.target_language);
    }
  }

  let best: string | null = null;
  let bestN = 0;
  for (const [lang, n] of counts) {
    if (n > bestN) {
      best = lang;
      bestN = n;
    }
  }
  return best ?? "zh";
}

export function normalizeLessonLang(
  raw: string | null | undefined,
): "zh" | "ja" | null {
  if (!raw) return null;
  const lower = raw.trim().toLowerCase();
  if (
    lower === "zh" ||
    lower.startsWith("zh-") ||
    lower === "cmn" ||
    lower === "chi" ||
    lower === "zho"
  ) {
    return "zh";
  }
  if (lower === "ja" || lower === "jp" || lower === "jpn" || lower.startsWith("ja-")) {
    return "ja";
  }
  return null;
}
